import React, { useMemo } from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';
import { ColorImage } from '../../admin/fabric/ColorImage';
import { StockDot } from '../../fabric/StockDot';
import { isUnavailable } from '../../../lib/fabricLabels';

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};
const colorDistance = (a, b) => {
  const ra = hexToRgb(a), rb = hexToRgb(b);
  if (!ra || !rb) return 999;
  return Math.sqrt(ra.reduce((s, v, i) => s + (v - rb[i]) ** 2, 0));
};

// Ranks still-available fabrics by closeness: same collection/material first, then similar colour and fabric type.
export const findAlternatives = (entry, all, limit = 4) =>
  all
    .filter((e) => e.id !== entry.id && !isUnavailable(e))
    .map((e) => {
      let score = colorDistance(entry.hex_color, e.hex_color);
      if (e.material_id && e.material_id === entry.material_id) score -= 120;
      else if (e.collection_id === entry.collection_id) score -= 80;
      else if (e.brand_name === entry.brand_name) score -= 20;
      if (entry.fabric_type && e.fabric_type === entry.fabric_type) score -= 40;
      if (entry.openness && e.openness === entry.openness) score -= 25;
      return { e, score };
    })
    .sort((a, b) => a.score - b.score)
    .slice(0, limit)
    .map((x) => x.e);

export const DiscontinuedAlternatives = ({ entry, allEntries = [], onPick }) => {
  const alternatives = useMemo(() => (entry?.discontinued ? findAlternatives(entry, allEntries) : []), [entry, allEntries]);
  if (!entry?.discontinued) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 space-y-2.5" data-testid="fabric-alternatives">
      <div className="flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" strokeWidth={2.5} />
        <div>
          <p className="text-sm font-medium text-red-800">This fabric is discontinued</p>
          <p className="text-xs text-red-700/80">{entry.stock_status === 'limited' ? 'Only remaining stock is available — order soon or pick a similar fabric below.' : 'Here are similar fabrics that are still available.'}</p>
        </div>
      </div>
      {alternatives.length > 0 ? (
        <div className="grid grid-cols-4 gap-2">
          {alternatives.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onPick?.(a)}
              className="group text-left rounded-lg overflow-hidden border border-white bg-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow]"
              title={`${a.name} · ${a.collection_name}${a.material_name ? ` › ${a.material_name}` : ''}`}
              data-testid={`fabric-alternative-${a.id}`}
            >
              <div className="relative aspect-square bg-neutral-100">
                <ColorImage src={a.image_url} alt={a.name} hex={a.hex_color} w={256} className="w-full h-full" />
                <StockDot status={a.stock_status} size="sm" className="absolute bottom-1 left-1" />
              </div>
              <div className="px-1.5 py-1">
                <p className="text-[11px] font-medium text-neutral-900 truncate">{a.name}</p>
                <p className="text-[9px] text-neutral-500 truncate">{a.material_name || a.collection_name}</p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-neutral-500">No close match available right now.</p>
      )}
      {alternatives.length > 0 && (
        <p className="text-[10px] text-neutral-500 flex items-center gap-1"><ArrowRight className="w-3 h-3" /> Tap a swatch to view it</p>
      )}
    </div>
  );
};

export default DiscontinuedAlternatives;
